import {BaseStage} from "./BaseStage";
import {initFlatPickerInput} from "../../../helpers/inputs/flatPicker/flatPicker";
import createElement from "../../../helpers/createElement";
import {scrollToElement} from "../../../helpers/scrolling/scrolling";
import {getReservedTicketTypeCardCopy} from "./ReservedSeatEntityLabels";
import {resolveTicketTypeAvailability} from "../availability/ticketTypeAvailability";
import {getSelectedDateCountLabel} from "./EventDatePresentation";
import {getContinuousDateRangeSummary} from "../continuousDateRange";

class DateSelectStage extends BaseStage {
    /** @var {Object<string,PublicEventResource.PublicEventResource[]>} */
    eventsByDate;
    flatpickrHtmlInitializedIntervalId;
    /** @var {HTMLElement} */
    templatesContainer;

    constructor(globalState) {
        super(globalState);

        // Init dom container
        this.domContainer = document.querySelector(`.stage.${this.getStageKey()}`)

        // Retrieve template container
        this.templatesContainer = document.querySelector('.templates .date-select-stage-template');

        // Clone template
        const stageWrapper = this.templatesContainer.querySelector('.stage-wrapper').cloneNode(true);

        // Prepare pending event dates to place in calendar
        //// Group events by their date
        //// Each key is a HTML date and the contents an array of events for that day
        /** @var {Object<string,PublicEventResource.PublicEventResource[]>} */
        this.eventsByDate = {};

        this.globalState.parentEvent.pendingDates.forEach(event => {
            if (this.eventsByDate[event.htmlDate] === undefined) {
                this.eventsByDate[event.htmlDate] = [];
            }

            this.eventsByDate[event.htmlDate].push(event);
        })

        //// Get enabled dates
        const enabledDatesArray = Object.keys(this.eventsByDate);

        // Init flatpickr instance
        const calendarElement = stageWrapper.querySelector('.dummy-input');

        this.flatPickrInstance = initFlatPickerInput(calendarElement, {
            inline: true,
            enable: enabledDatesArray,
            // Because events come presorted, first and last
            // dates in the array are also the first and last shows
            minDate: enabledDatesArray[0],
            maxDate: enabledDatesArray[enabledDatesArray.length - 1],

            // Update HTML on change
            onChange: () => {
                this.initializeFlatpickrDateHtml();

                if (this.isMultipleDatesSelectionMode()) {
                    this.renderMultiDateToggleStates();
                    return;
                }

                this.handleDateChange();
            },
            onMonthChange: () => {
                this.initializeFlatpickrDateHtml();
                this.renderMultiDateToggleStates();
            },
        })

        // Update HTML on a interval, as a fallback
        this.flatpickrHtmlInitializedIntervalId = setInterval(() => {
            this.initializeFlatpickrDateHtml();
        }, 16)

        // Store date info wrapper
        this.dateInfoWrapper = stageWrapper.querySelector('.show-info-container-wrapper');

        if (this.isMultipleDatesSelectionMode()) {
            this.renderMultiDateMobileList(stageWrapper);
        }

        // Append stage to DOM
        this.domContainer.appendChild(stageWrapper);

        // Handle iframe resizing and aspect ratio capping
        window.addEventListener('message', (event) => {
            if (event.data && event.data.type === 'seat-map-dimensions') {
                const {width, height} = event.data;
                const iframe = this.domContainer.querySelector('.seat-map-iframe');
                const mapWrapper = iframe ? iframe.closest('.map-wrapper') : null;

                if (iframe && mapWrapper) {
                    const ratio = height / width;
                    const maxAxis = 400;

                    let targetWidth;
                    if (width >= height) {
                        targetWidth = maxAxis;
                    } else {
                        targetWidth = maxAxis / ratio;
                    }

                    // Cap by viewport width (minus a little padding) so it never overflows on tiny screens
                    const viewportWidth = Math.max(
                        document.documentElement?.clientWidth || 0,
                        window.innerWidth || 0
                    );
                    const maxAllowedWidth = Math.max(0, viewportWidth - 20);
                    if (targetWidth > maxAllowedWidth) {
                        targetWidth = maxAllowedWidth;
                    }

                    // Apply constraints to the wrapper
                    mapWrapper.style.width = `${targetWidth}px`;

                    const updateHeight = () => {
                        if (!iframe.isConnected) {
                            window.removeEventListener('resize', updateHeight);
                            return;
                        }
                        const currentWidth = iframe.offsetWidth;
                        iframe.style.height = `${currentWidth * ratio}px`;
                    };

                    // Prevent multiple listeners if message is sent again
                    if (iframe._seatMapResizeHandler) {
                        window.removeEventListener('resize', iframe._seatMapResizeHandler);
                    }
                    iframe._seatMapResizeHandler = updateHeight;

                    updateHeight();
                    window.addEventListener('resize', updateHeight);
                }
            }
        });

        // Run base class initializations
        this.initSharedComponents();

        if (this.isMultipleDatesSelectionMode()) {
            const nextStageButton = stageWrapper.querySelector('.next-stage-button');

            if (nextStageButton) {
                nextStageButton.innerHTML = 'CONFIRM SELECTION';
            }
        } else {
            const nextStageButton = stageWrapper.querySelector('.next-stage-button');

            if (nextStageButton) {
                nextStageButton.innerHTML = `CONFIRM ${this.globalState.parentEvent.dateUnitLabelSingular.toUpperCase()}`;
            }
        }

        if (this.shouldPrefillSelectedDate()) {
            this.prefillSelectedDate();
        }

    }

    /**
     * @return {PublicTicketsFormDefs.Stage}
     */
    getStageKey() {
        // noinspection JSValidateTypes
        return 'date-select';
    }

    clearDom() {
        // Clear stage HTML
        this.domContainer.innerHTML = '';

        // Stop interval
        clearInterval(this.flatpickrHtmlInitializedIntervalId);
    }

    prefillSelectedDate() {
        const selectedDate = this.globalState.selectedDate;

        if (!selectedDate || !selectedDate.htmlDate) {
            return;
        }

        const dayShows = this.eventsByDate[selectedDate.htmlDate] || [];

        if (!dayShows.length) {
            return;
        }

        this.flatPickrInstance.setDate(selectedDate.htmlDate, !this.isMultipleDatesSelectionMode());

        if (this.isMultipleDatesSelectionMode()) {
            this.renderMultiDateToggleStates();
            this.renderMultiDateInfoContainer();

            return;
        }

        if (dayShows.length === 1) {
            return;
        }

        const selectedDateIndex = dayShows.findIndex(dayShow => dayShow.id === selectedDate.id);

        if (selectedDateIndex < 0) {
            return;
        }

        const timeSelect = this.domContainer.querySelector('.performance-time-select');

        if (!timeSelect) {
            return;
        }

        timeSelect.value = `${selectedDateIndex}`;
        timeSelect.dispatchEvent(new Event('change'));
    }

    shouldPrefillSelectedDate() {
        return true;
    }

    isMultipleDatesSelectionMode() {
        return this.globalState.isMultipleDatesSelectionMode();
    }

    isContinuousDateRangeMode() {
        return this.globalState.isContinuousDateRangeMode();
    }

    /**
     * Flatpickr does not let us easily change the days HTML content
     * So this takes care of doing it
     */
    initializeFlatpickrDateHtml() {
        // Check for all non initialized elements
        this.domContainer.querySelectorAll(".flatpickr-day:not(.initialized)")
            .forEach(calendarDayContainer => {
                const defaultContent = calendarDayContainer.innerHTML;

                let customContentString = '';

                // Create Date instance from aria label
                const dateInstance = new Date(calendarDayContainer.ariaLabel);

                const dayName = dateInstance.toLocaleDateString('en-US', {
                    weekday: 'long',
                }).toUpperCase();

                // Retrieve related event date
                const htmlDate = dateInstance.toISOString().split('T')[0];

                const relatedEvents = this.eventsByDate[htmlDate];

                let eventTimesString = '';

                if (relatedEvents) {
                    if (this.isMultipleDatesSelectionMode()) {
                        eventTimesString = relatedEvents.map(event => this.getMultiDateToggleHtml(event)).join('\n');
                    } else {
                        eventTimesString = relatedEvents.map(event => {
                            return `<div class="event-time">${event.timeOnly}</div>`;
                        }).join('\n');
                    }
                }

                // Retrieve custom content if exists
                const dateCustomContentElement = document.querySelector(`.pre-show-entertainment-calendar *[data-date="${calendarDayContainer.ariaLabel}"]`)

                // Enabled only for christmas event with id 126
                if (dateCustomContentElement && this.globalState.parentEvent.id === '126') {
                    customContentString = dateCustomContentElement.innerHTML;
                }

                // Update HTML
                calendarDayContainer.innerHTML = `
                    <div class="border-wrapper">
                        <div class="large-screen d-none d-md-block">
                             <div class="day-header">
                                <div class="day-number">
                                    ${defaultContent}
                                </div>
                                <div
                                    class="day-name small text-muted pl-2">
                                    ${dayName}
                                </div>
                            </div>
                            <div class="date-details px-3">
                                <div class="times-container">
                                    ${eventTimesString}
                                </div>
                                <div class="custom-content-container">
                                    ${customContentString}
                                </div>
                            </div>
                        </div>
                        <div class="small-screen d-block d-md-none">
                            <div class="day-number">
                                ${defaultContent}
                            </div>
                        </div>
                    </div>
                `;

                calendarDayContainer.classList.add('initialized')
                // Also add a data- attribute with the date in HTML format
                calendarDayContainer.dataset.date = htmlDate;

                if (this.isMultipleDatesSelectionMode()) {
                    this.initializeMultiDateToggles(calendarDayContainer);
                }
            })

        this.renderMultiDateToggleStates();
    }

    /**
     * @param {PublicEventResource.PublicEventResource} eventDate
     * @return {string}
     */
    getMultiDateToggleHtml(eventDate) {
        return `<button
            type="button"
            class="event-time multi-date-toggle"
            data-multi-date-toggle
            data-event-date-id="${eventDate.id}"
            aria-pressed="false"
        >${this.getEventDateChoiceLabel(eventDate)}</button>`;
    }

    /**
     * @param {HTMLElement} calendarDayContainer
     */
    initializeMultiDateToggles(calendarDayContainer) {
        calendarDayContainer.querySelectorAll('[data-multi-date-toggle]').forEach(toggle => {
            toggle.addEventListener('click', event => {
                event.preventDefault();
                event.stopPropagation();

                this.toggleMultiDateSelection(toggle.dataset.eventDateId);
            });
        });
    }

    /**
     * @param {string} eventDateId
     */
    toggleMultiDateSelection(eventDateId) {
        const eventDate = this.globalState.parentEvent.pendingDates.find(candidate => candidate.id === eventDateId);

        if (!eventDate) {
            return;
        }

        if (this.isContinuousDateRangeMode()) {
            this.globalState.setContinuousRangeDate(eventDate, this.globalState.parentEvent.pendingDates);
            this.renderMultiDateToggleStates();
            this.renderMultiDateInfoContainer();
            this.updateNextButtonStatus();
            return;
        }

        const nextSelectedDates = this.globalState.selectedDates.some(selectedDate => selectedDate.id === eventDateId)
            ? this.globalState.selectedDates.filter(selectedDate => selectedDate.id !== eventDateId)
            : [...this.globalState.selectedDates, eventDate];

        this.globalState.setSelectedDates(nextSelectedDates);
        this.renderMultiDateToggleStates();
        this.renderMultiDateInfoContainer();
        this.updateNextButtonStatus();
    }

    renderMultiDateToggleStates() {
        if (!this.isMultipleDatesSelectionMode()) {
            return;
        }

        const selectedDateIds = new Set(this.globalState.selectedDates.map(eventDate => eventDate.id));

        this.domContainer.querySelectorAll('[data-multi-date-toggle]').forEach(toggle => {
            const isSelected = selectedDateIds.has(toggle.dataset.eventDateId);

            toggle.classList.toggle('selected', isSelected);
            toggle.setAttribute('aria-pressed', isSelected ? 'true' : 'false');
        });

        this.domContainer.querySelectorAll('[data-multi-date-mobile-toggle]').forEach(toggle => {
            const isSelected = selectedDateIds.has(toggle.dataset.eventDateId);

            toggle.classList.toggle('selected', isSelected);
            toggle.setAttribute('aria-pressed', isSelected ? 'true' : 'false');
        });

        this.domContainer.querySelectorAll('.flatpickr-day[data-date]').forEach(day => {
            const dayEvents = this.eventsByDate[day.dataset.date] || [];
            const hasSelectedEvent = dayEvents.some(eventDate => selectedDateIds.has(eventDate.id));

            day.classList.toggle('selected', hasSelectedEvent);
        });
    }

    /**
     * @param {HTMLElement} stageWrapper
     */
    renderMultiDateMobileList(stageWrapper) {
        const groupedEventDates = Object.entries(this.eventsByDate);

        if (groupedEventDates.length === 0) {
            return;
        }

        const groupsHtml = groupedEventDates.map(([htmlDate, eventDates]) => {
            const exampleEventDate = eventDates[0];
            const togglesHtml = eventDates.map(eventDate => this.getMultiDateMobileToggleHtml(eventDate)).join('\n');

            return `<div class="multi-date-mobile-group" data-multi-date-mobile-group data-event-html-date="${htmlDate}">
                <div class="multi-date-mobile-date">${exampleEventDate.longDateOnly}</div>
                <div class="multi-date-mobile-times">
                    ${togglesHtml}
                </div>
            </div>`;
        }).join('\n');

        const mobileList = createElement(`<div class="multi-date-mobile-list d-md-none" data-multi-date-mobile-list>
            <div class="multi-date-mobile-title">${this.globalState.parentEvent.dateSelectionPrompt}</div>
            <div class="multi-date-mobile-help">${this.isContinuousDateRangeMode() ? 'Choose one date, or choose another to include every available date between them.' : `Choose each ${this.globalState.parentEvent.dateUnitLabelSingular.toLowerCase()}${this.globalState.parentEvent.eventDateGranularity === 'timed' && this.globalState.parentEvent.showTimeWithEventDates ? ' and time' : ''} you want to attend.`}</div>
            ${groupsHtml}
        </div>`);

        mobileList.querySelectorAll('[data-multi-date-mobile-toggle]').forEach(toggle => {
            toggle.addEventListener('click', event => {
                event.preventDefault();

                this.toggleMultiDateSelection(toggle.dataset.eventDateId);
            });
        });

        const calendarContainer = stageWrapper.querySelector('.calendar-container');

        calendarContainer.classList.add('d-none', 'd-md-block');
        calendarContainer.after(mobileList);
    }

    /**
     * @param {PublicEventResource.PublicEventResource} eventDate
     * @return {string}
     */
    getMultiDateMobileToggleHtml(eventDate) {
        return `<button
            type="button"
            class="multi-date-mobile-toggle"
            data-multi-date-mobile-toggle
            data-event-date-id="${eventDate.id}"
            aria-pressed="false"
        >${this.getEventDateChoiceLabel(eventDate)}</button>`;
    }

    getEventDateChoiceLabel(eventDate) {
        const dayEvents = this.eventsByDate[eventDate.htmlDate] || [];
        const time = eventDate.timeOnly || 'Select';
        const matchingTimeEvents = dayEvents.filter(candidate => {
            return candidate.eventDateTimestamp === eventDate.eventDateTimestamp;
        });

        if (matchingTimeEvents.length <= 1) {
            return time;
        }

        const duplicateIndex = matchingTimeEvents.findIndex(candidate => candidate.id === eventDate.id) + 1;
        const title = eventDate.title || 'Performance';

        return `${time} - ${title} ${duplicateIndex}`;
    }

    renderMultiDateInfoContainer() {
        if (this.globalState.selectedDates.length === 0) {
            this.dateInfoWrapper.innerHTML = '';
            this.disableNextStageButton();

            return;
        }

        this.renderDateInfoContainer(this.globalState.selectedDates);
    }

    handleDateChange() {
        // Retrieve related date shows
        const date = this.flatPickrInstance.input.value;
        const shows = this.eventsByDate[date];

        if (shows.length === 1) {
            // Default to first available date
            this.globalState.setEventDate(shows[0]);
        } else {
            // Clear event date
            this.globalState.setEventDate(undefined);
        }

        // Disable stage advance
        this.disableNextStageButton();

        // Display event data
        this.renderDateInfoContainer(shows);

        // Scroll to recently added container, after a bit
        const showInfoContainer = this.domContainer.querySelector('.show-info-container-wrapper');

        if (showInfoContainer) {
            scrollToElement(showInfoContainer, 10);
        } else {
            // Fallback, scroll to bottom
            setTimeout(() => {
                window.scrollTo({
                    behavior: 'smooth',
                    top: 1000000,
                })
            }, 16)
        }
    }

    /**
     * @param {PublicEventResource.PublicEventResource[]} dayShows
     */
    renderDateInfoContainer(dayShows) {
        const state = this.globalState;
        const parentEvent = state.parentEvent;

        // Clear existing HTML
        this.dateInfoWrapper.innerHTML = '';

        // Clone new container
        const container = this.templatesContainer.querySelector('.show-info-container').cloneNode(true);

        // Set event name & description
        container.querySelector('.show-name').innerHTML = parentEvent.title;
        container.querySelector('.show-description').innerHTML = parentEvent.description;


        // Setup general and reserved price data and availability
        const generalAdmissionCard = container.querySelector('.ticket-type-card.general-admission')
        const reservedCard = container.querySelector('.ticket-type-card.reserved')

        generalAdmissionCard.querySelector('.price-range').innerHTML = '';

        reservedCard.querySelector('.price-range').innerHTML = '';

        this.updateTicketTypeCardAvailability(generalAdmissionCard, reservedCard, dayShows);
        this.updateReservedTicketTypeCardCopy(reservedCard, dayShows);
        this.updateGeneralAdmissionTicketTypeCardCopy(generalAdmissionCard);

        // Set selected date
        let dateAndTimeContainer = container.querySelector('.date-and-time-container');

        // Pick the first day show to display in the section header
        const exampleDayShow = dayShows[0];

        if (this.isMultipleDatesSelectionMode()) {
            dateAndTimeContainer.innerHTML = this.getMultiDateSummaryHtml();
        } else if (dayShows.length > 1) {
            dateAndTimeContainer.innerHTML =
                `<div>${exampleDayShow.longDateOnly} <br>
                    <div class="time-select-container">
                        <select class="form-control performance-time-select mt-1" name="event_date_time" autocomplete="off">
                            <option value="" disabled selected>Select Performance</option>
                        </select>
                    </div>
                </div>`;

            /** @var {HTMLSelectElement} */
            const timeSelect = dateAndTimeContainer.querySelector('select');

            // Create options for each time, with the day's date index as the value
            dayShows.forEach((date, dateIndex) => {
                timeSelect.appendChild(createElement(`<option data-event-date-id="${date.id}" data-event-date-timestamp="${date.eventDateTimestamp}" value="${dateIndex}">${date.timeOnly}</option>`));
            })

            // Update global state on date change
            timeSelect.addEventListener('change', () => {
                if (timeSelect.value) {
                    // Set date
                    const selectedSeatType = this.globalState.selectedSeatType;

                    this.globalState.setEventDate(dayShows[timeSelect.value]);

                    // Preserve previous selection if it exists
                    if (selectedSeatType) {
                        // noinspection JSCheckFunctionSignatures
                        this.globalState.setSeatType(selectedSeatType);
                        this.applyEntryIntentState(selectedSeatType);
                    }

                    this.updateTicketTypeCardAvailability(generalAdmissionCard, reservedCard, dayShows);
                    this.updateReservedTicketTypeCardCopy(reservedCard, dayShows);
                    this.updateGeneralAdmissionTicketTypeCardCopy(generalAdmissionCard);
                    this.autoSelectSingleRenderedSeatType(container);

                    // Check if we can continue
                    this.updateNextButtonStatus();
                }
            });
        } else {
            dateAndTimeContainer.innerHTML = `<div>${state.selectedDate.longDateOnly}${state.selectedDate.timeOnly ? ` <br> ${state.selectedDate.timeOnly}` : ''}</div>`;
        }

        // Set custom content
        // Retrieve custom content if exists

        // Setup ticket selection
        [generalAdmissionCard, reservedCard].forEach(ticketTypeCard => {
            const ticketInput = ticketTypeCard.querySelector(`input`);

            ticketInput.addEventListener('change', () => {
                this.handleSeatTypeSelect(ticketTypeCard);
            })

            // Trigger show selection
            ticketTypeCard.addEventListener('click', () => {
                ticketInput.checked = true;
                ticketInput.dispatchEvent(new InputEvent('change'));
            })
        })

        // Update seat map image
        const seatImageElement = container.querySelector('.seat-map-image');
        const seatIframeElement = container.querySelector('.seat-map-iframe');
        const mapContainer = seatImageElement.closest('.map-container');
        const route = state.parentEvent.seatMapImageRoute;

        // Check if we have a seat map image
        if (route) {
            // Display seat map
            mapContainer.classList.remove('d-none');

            if (route.includes('seat-map-html')) {
                // It's an SVG HTML page
                seatImageElement.classList.add('d-none');
                seatIframeElement.classList.remove('d-none');
                seatIframeElement.src = route;
            } else {
                // It's a regular image
                seatIframeElement.classList.add('d-none');
                seatImageElement.classList.remove('d-none');

                // Apply similar capping logic for images
                seatImageElement.onload = () => {
                    const width = seatImageElement.naturalWidth;
                    const height = seatImageElement.naturalHeight;
                    const ratio = height / width;
                    const maxAxis = 400;

                    let targetWidth;
                    if (width >= height) {
                        targetWidth = maxAxis;
                    } else {
                        targetWidth = maxAxis / ratio;
                    }

                    const mapWrapper = seatImageElement.closest('.map-wrapper');
                    if (mapWrapper) {
                        mapWrapper.style.maxWidth = `${targetWidth}px`;
                    }
                };

                seatImageElement.src = route;
            }
        } else {
            // Hide seat map
            mapContainer.classList.add('d-none');
            seatImageElement.src = '';
            seatIframeElement.src = '';
        }

        // Update banner image
        const homeImageElement = container.querySelector('.home-map-image');
        const homeContainer = homeImageElement.closest('.map-container');

        // Check if we have a seat map image
        // TEMP: force hide home image
        homeContainer.classList.add('d-none');
        homeImageElement.src = '';

        // if (state.parentEvent.homeImageRoute) {
        //     // Display home image
        //     homeContainer.classList.remove('d-none');
        //     homeImageElement.src = state.parentEvent.homeImageRoute;
        // } else {
        //     // Hide home image
        //     homeContainer.classList.add('d-none');
        //     homeImageElement.src = '';
        // }

        // Append to DOM
        this.dateInfoWrapper.appendChild(container);

        this.applySelectedSeatTypeToRenderedCards(container);
        this.autoSelectSingleRenderedSeatType(container);
        this.updateNextButtonStatus();
    }

    /**
     * @param {HTMLElement} container
     */
    applySelectedSeatTypeToRenderedCards(container) {
        const selectedSeatType = this.globalState.selectedSeatType;

        if (!selectedSeatType) {
            return;
        }

        const selectedCard = container.querySelector(`.ticket-type-card.${selectedSeatType}:not(.d-none)`);

        if (!selectedCard) {
            return;
        }

        selectedCard.classList.add('selected');
        selectedCard.querySelector('input').checked = true;
    }

    /**
     * @param {HTMLElement} container
     */
    autoSelectSingleRenderedSeatType(container) {
        if (!this.shouldAutoSelectSingleRenderedSeatType()) {
            return;
        }

        const selectedSeatType = this.globalState.selectedSeatType;

        if (selectedSeatType && container.querySelector(`.ticket-type-card.${selectedSeatType}:not(.d-none)`)) {
            return;
        }

        const visibleCards = this.getVisibleSeatTypeCards(container);

        if (visibleCards.length !== 1) {
            return;
        }

        this.handleSeatTypeSelect(visibleCards[0]);
    }

    /**
     * @param {HTMLElement} container
     * @return {HTMLElement[]}
     */
    getVisibleSeatTypeCards(container) {
        const visibleCards = [];

        container.querySelectorAll('.ticket-type-card.reserved, .ticket-type-card.general-admission')
            .forEach(ticketTypeCard => {
                if (
                    ticketTypeCard instanceof HTMLElement &&
                    ticketTypeCard.classList.contains('d-none') === false
                ) {
                    visibleCards.push(ticketTypeCard);
                }
            });

        return visibleCards;
    }

    shouldAutoSelectSingleRenderedSeatType() {
        return true;
    }

    getMultiDateSummaryHtml() {
        const selectedDates = this.globalState.selectedDates;
        const rangeSummary = this.isContinuousDateRangeMode()
            ? getContinuousDateRangeSummary(
                this.globalState.parentEvent.pendingDates,
                selectedDates,
                this.useOperationalContinuousRangeSummary()
            )
            : '';

        return `<div class="multi-date-selected-summary" data-multi-date-selected-summary>
            <div data-multi-date-selected-count>${getSelectedDateCountLabel(this.globalState.parentEvent, selectedDates.length)}</div>
            ${rangeSummary ? `<div class="continuous-range-summary" data-continuous-range-summary>${rangeSummary}</div>` : ''}
        </div>`;
    }

    useOperationalContinuousRangeSummary() {
        return false;
    }

    /**
     * @param {PublicEventResource.PublicEventResource[]} dayShows
     */
    getTicketTypeAvailabilityForCards(dayShows) {
        return resolveTicketTypeAvailability({
            selectedDates: this.globalState.getPurchasedDates(),
            dayShows,
            fallbackSource: this.globalState.parentEvent,
        }).availabilitySource;
    }

    /**
     * @param {HTMLElement} generalAdmissionCard
     * @param {HTMLElement} reservedCard
     * @param {PublicEventResource.PublicEventResource[]} dayShows
     */
    updateTicketTypeCardAvailability(generalAdmissionCard, reservedCard, dayShows) {
        const {isGeneralAdmissionAvailable, isReservedAvailable} = resolveTicketTypeAvailability({
            selectedDates: this.globalState.getPurchasedDates(),
            dayShows,
            fallbackSource: this.globalState.parentEvent,
        });

        generalAdmissionCard.classList.toggle('d-none', isGeneralAdmissionAvailable === false);
        reservedCard.classList.toggle('d-none', isReservedAvailable === false);

        this.clearUnavailableSeatTypeSelection(generalAdmissionCard, reservedCard);
    }

    /**
     * @param {HTMLElement} reservedCard
     * @param {PublicEventResource.PublicEventResource[]} dayShows
     */
    updateReservedTicketTypeCardCopy(reservedCard, dayShows) {
        const availabilitySource = this.getTicketTypeAvailabilityForCards(dayShows);
        const copy = getReservedTicketTypeCardCopy(availabilitySource?.reservedEntityLabels);

        const reservedEntryLabel = this.getReservedEntryLabel(copy.title);
        this.globalState.effectiveReservedEntryLabel = reservedEntryLabel;
        reservedCard.querySelector('.show-name').textContent = reservedEntryLabel;
        reservedCard.querySelector('.show-description').textContent = copy.description;
    }

    updateGeneralAdmissionTicketTypeCardCopy(generalAdmissionCard) {
        generalAdmissionCard.querySelector('.show-name').textContent = this.getGeneralAdmissionEntryLabel();
    }

    getReservedEntryLabel(defaultLabel = 'RESERVED TICKETS') {
        const override = String(this.globalState.parentEvent?.reservedEntryLabel || '').trim();

        return override || defaultLabel;
    }

    getGeneralAdmissionEntryLabel() {
        const override = String(this.globalState.parentEvent?.generalAdmissionEntryLabel || '').trim();

        return override || 'GENERAL ADMISSION';
    }

    applyEntryIntentState(seatType) {
        if (!this.globalState.isPublicMixedCompositionEnabled()) {
            return;
        }

        if (seatType === 'general-admission') {
            this.globalState.setGeneralAdmissionEnabled(true);
            return;
        }

        this.globalState.setGeneralAdmissionEnabled(
            this.globalState.hasPositiveGeneralAdmissionTicketCounts()
        );
    }

    /**
     * @param {HTMLElement} generalAdmissionCard
     * @param {HTMLElement} reservedCard
     */
    clearUnavailableSeatTypeSelection(generalAdmissionCard, reservedCard) {
        const selectedSeatType = this.globalState.selectedSeatType;

        if (!selectedSeatType) {
            return;
        }

        const selectedCard = selectedSeatType === 'general-admission'
            ? generalAdmissionCard
            : reservedCard;

        if (selectedCard.classList.contains('d-none') === false) {
            return;
        }

        selectedCard.classList.remove('selected');
        selectedCard.querySelector('input').checked = false;
        this.globalState.setSeatType(undefined);
    }

    /**
     * @param {HTMLElement} ticketTypeContainer
     */
    handleSeatTypeSelect(ticketTypeContainer) {
        /** @var {HTMLInputElement} */
        const ticketTypeInput = ticketTypeContainer.querySelector(`input`);

        // Clear selection from other tickets
        this.domContainer.querySelectorAll('.ticket-type-card').forEach(ticketCard => {
            ticketCard.classList.remove('selected');
            ticketCard.querySelector('input').checked = false;
        })

        // Mark show as selected
        ticketTypeInput.checked = true;
        // And add selected class
        ticketTypeContainer.classList.add('selected');

        // Save global state
        // noinspection JSCheckFunctionSignatures
        this.globalState.setSeatType(ticketTypeInput.value);
        this.applyEntryIntentState(ticketTypeInput.value);

        this.updateNextButtonStatus();
    }

    updateNextButtonStatus() {
        if (
            this.globalState.parentEvent &&
            this.globalState.selectedSeatType &&
            this.globalState.selectedDate
        ) {
            this.enableNextStageButton();
        } else {
            this.disableNextStageButton();
        }
    }
}

export {DateSelectStage};
